/**
 * Public Company Profile Page
 * SEO-optimized employer profile with trust signals
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Building2, MapPin, Globe, Mail, Calendar, Star, Clock,
  CheckCircle2, Shield, Users, Briefcase, ChevronLeft,
  Award, Languages, MessageSquare, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { ShareButton } from '@/components/ShareButton';
import { TrustScoreWidget } from '@/components/TrustScoreWidget';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { useAuth, useLanguage } from '@/lib/context';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function CompanyProfilePublicPage() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [trustScore, setTrustScore] = useState(null);

  useEffect(() => {
    fetchCompanyProfile();
  }, [slug]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/employer/public/${slug}`);
      
      if (!response.ok) {
        throw new Error('Company not found');
      }
      
      const data = await response.json();
      setCompany(data);
      
      // Fetch active jobs by this company
      try {
        const jobsRes = await fetch(`${API}/api/jobs?company_id=${data.user_id || data.id}&limit=5`);
        if (jobsRes.ok) {
          const jobsData = await jobsRes.json();
          setActiveJobs(jobsData.jobs || []);
        }
      } catch (e) {
        console.log('Could not fetch jobs');
      }
      
      // Fetch trust score for this company owner
      try {
        if (data.user_id || data.owner_id) {
          const trustRes = await fetch(`${API}/api/reputation/trust-score/${data.user_id || data.owner_id}`);
          if (trustRes.ok) {
            const trustData = await trustRes.json();
            setTrustScore(trustData);
          }
        }
      } catch (e) {
        console.log('Could not fetch trust score');
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CompanyProfileSkeleton />;
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h1 className="text-xl font-semibold mb-2">{t('companyProfile.companyNotFound')}</h1>
            <p className="text-gray-600 mb-4">
              {t('companyProfile.companyNotFoundDesc')}
            </p>
            <Button onClick={() => navigate('/jobs')}>
              {t('companyProfile.browseJobs')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const trustSignals = company.trust_signals || {};
  const isVerified = company.verification_status === 'verified';

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{company.meta_title || `${company.company_name} | KEIKKA`}</title>
        <meta name="description" content={company.meta_description || company.description?.slice(0, 160)} />
        <link rel="canonical" href={`https://keikka.work/company/${company.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={company.company_name} />
        <meta property="og:description" content={company.meta_description || company.description?.slice(0, 200)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://keikka.work/company/${company.slug}`} />
        {company.logo_url && <meta property="og:image" content={company.logo_url} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={company.company_name} />
        <meta name="twitter:description" content={company.meta_description || company.description?.slice(0, 200)} />
        
        {/* Schema.org JSON-LD */}
        {company.schema_org && (
          <script type="application/ld+json">
            {JSON.stringify(company.schema_org)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Navigation */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Button>
            <div className="flex items-center gap-2">
              <ShareButton
                title={company.company_name}
                description={company.description?.slice(0, 100)}
                entityType="company"
                entityId={company.id}
                url={`${window.location.origin}/company/${company.slug}`}
              />
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Logo */}
              <Avatar className="h-24 w-24 md:h-32 md:w-32 ring-4 ring-gray-100">
                <AvatarImage src={company.logo_url} alt={company.company_name} />
                <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  {company.company_name?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {/* Company Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {company.company_name}
                      </h1>
                      {trustScore && (
                        <TrustScoreBadge score={trustScore.trust_score} t={t} />
                      )}
                      {isVerified && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {t('companyProfile.verified')}
                        </Badge>
                      )}
                      {trustSignals.has_active_subscription && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          <Shield className="h-3 w-3 mr-1" />
                          {t('companyProfile.premium')}
                        </Badge>
                      )}
                    </div>

                    {/* Location */}
                    {(company.city || company.country) && (
                      <p className="text-gray-600 mt-1 flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {[company.city, company.country].filter(Boolean).join(', ')}
                      </p>
                    )}

                    {/* Industry tags */}
                    {company.industries?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {company.industries.slice(0, 3).map((industry, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {industry}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Trust Signals Row */}
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  {trustSignals.completed_jobs_count > 0 && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Briefcase className="h-4 w-4" />
                      <span>{trustSignals.completed_jobs_count} {t('companyProfile.jobsCompleted')}</span>
                    </div>
                  )}
                  {trustSignals.community_score > 0 && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span>{trustSignals.community_score.toFixed(1)} ({trustSignals.total_reviews} {t('companyProfile.reviews')})</span>
                    </div>
                  )}
                  {trustSignals.avg_response_time_hours && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{t('companyProfile.respondsIn')} ~{Math.round(trustSignals.avg_response_time_hours)}h</span>
                    </div>
                  )}
                  {company.years_active && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{company.years_active} {t('companyProfile.yearsInBusiness')}</span>
                    </div>
                  )}
                </div>

                {/* AI Highlights */}
                {trustSignals.ai_highlights?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {trustSignals.ai_highlights.map((highlight, i) => (
                      <Badge key={i} className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                        {highlight}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* About */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {t('companyProfile.about')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {company.description_ai || company.description}
                  </p>
                </CardContent>
              </Card>

              {/* Services */}
              {company.services_offered?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5" />
                      {t('companyProfile.servicesOffered')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {company.services_offered.map((service, i) => (
                        <Badge key={i} variant="secondary" className="px-3 py-1">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Active Jobs */}
              {activeJobs.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {t('companyProfile.openPositions')}
                      </span>
                      <Link to={`/jobs?company=${company.id}`}>
                        <Button variant="ghost" size="sm">
                          {t('companyProfile.viewAllJobs')}
                          <ExternalLink className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {activeJobs.map((job) => (
                        <Link
                          key={job.id}
                          to={`/jobs/${job.id}`}
                          className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <h4 className="font-medium text-gray-900">{job.title}</h4>
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3" />
                            {[job.city, job.country].filter(Boolean).join(', ')}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Gallery */}
              {company.gallery_images?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('companyProfile.gallery')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {company.gallery_images.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt={`${company.company_name} gallery ${i + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - CTA & Details */}
            <div className="space-y-6">
              {/* Trust Score Widget */}
              {(trustScore || company.user_id || company.owner_id) && (
                <TrustScoreWidget 
                  userId={company.user_id || company.owner_id}
                  initialData={trustScore}
                  variant="full"
                  t={t}
                />
              )}
              
              {/* CTA Card */}
              <Card className="sticky top-20">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">{t('companyProfile.connectWith')} {company.company_name}</h3>
                  
                  {/* Primary CTA based on company settings */}
                  {company.primary_cta === 'book_time' && company.calendar_enabled ? (
                    <Button className="w-full mb-3" size="lg">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('companyProfile.bookAppointment')}
                    </Button>
                  ) : company.primary_cta === 'request_quote' ? (
                    <Button className="w-full mb-3" size="lg">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      {t('companyProfile.requestQuote')}
                    </Button>
                  ) : (
                    <Button className="w-full mb-3" size="lg" onClick={() => {
                      if (!user) {
                        toast.info(t('auth.loginRequired'));
                        navigate('/login');
                      } else {
                        toast.info('Contact unlock coming soon');
                      }
                    }}>
                      <Mail className="h-4 w-4 mr-2" />
                      {t('companyProfile.contactCompany')}
                    </Button>
                  )}

                  {/* Website link */}
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-blue-600 hover:underline text-sm"
                    >
                      <Globe className="h-4 w-4" />
                      {t('companyProfile.visitWebsite')}
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Details Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t('companyProfile.companyDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Regions Served */}
                  {company.regions_served?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('companyProfile.regionsServed')}</p>
                      <div className="flex flex-wrap gap-1">
                        {company.regions_served.map((region, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {region}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {company.languages_spoken?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                        <Languages className="h-4 w-4" />
                        {t('companyProfile.languages')}
                      </p>
                      <p className="text-gray-700">
                        {company.languages_spoken.join(', ')}
                      </p>
                    </div>
                  )}

                  {/* Certifications */}
                  {company.certifications?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                        <Award className="h-4 w-4" />
                        {t('companyProfile.certifications')}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {company.certifications.map((cert, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {cert}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Member Since */}
                  {company.created_at && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('companyProfile.memberSince')}</p>
                      <p className="text-gray-700">
                        {new Date(company.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Loading skeleton
function CompanyProfileSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex gap-6">
            <Skeleton className="h-32 w-32 rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyProfilePublicPage;
